const CACHE_KEY = 'cdcc_payapp_recent_uploads';
const MAX_ENTRIES = 3;

export interface CachedPayappEntry {
  id: string;
  displayName: string;
  savedAt: number;
  // Only OCR-derived data — project/draw resolved at runtime from dialog context
  state: {
    parsedTable: any[];
    coverFields: any;
    sourceTargetHeadersMapping: Record<string, string>;
  };
}

export function getCachedPayappUploads(): CachedPayappEntry[] {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as CachedPayappEntry[];
  } catch {
    return [];
  }
}

export function savePayappUploadToCache(entry: CachedPayappEntry): void {
  try {
    const existing = getCachedPayappUploads();
    // Upsert by id (same project+draw replaces old entry), keep latest MAX_ENTRIES
    const updated = [entry, ...existing.filter(e => e.id !== entry.id)].slice(0, MAX_ENTRIES);
    localStorage.setItem(CACHE_KEY, JSON.stringify(updated));
  } catch {
    // Silently swallow QuotaExceededError and any other storage errors
  }
}

export function removeCachedPayappUpload(id: string): void {
  try {
    const existing = getCachedPayappUploads();
    localStorage.setItem(CACHE_KEY, JSON.stringify(existing.filter(e => e.id !== id)));
  } catch {
    // ignore
  }
}

/** Seeds entries from payappSeed.ts into the cache (skips any id already present). */
export function seedPayappCache(seeds: CachedPayappEntry[]): void {
  if (!seeds.length) return;
  try {
    const existing = getCachedPayappUploads();
    const existingIds = new Set(existing.map(e => e.id));
    const toAdd = seeds.filter(s => !existingIds.has(s.id));
    if (!toAdd.length) return;
    const updated = [...toAdd, ...existing].slice(0, MAX_ENTRIES);
    localStorage.setItem(CACHE_KEY, JSON.stringify(updated));
  } catch {
    // ignore
  }
}

export function relativeTime(savedAt: number): string {
  const diff = Date.now() - savedAt;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
